/**
 * The book the trader sees: orders and positions, folded from the event stream.
 *
 * ## Exactly once, and never by accident
 *
 * `applyEvent` refuses any event whose `seq` is at or below the book's cursor.
 * That is the whole of the client's duplicate protection, and it is enough
 * because `account_seq` is gapless and monotonic per account (docs/10 §3.4):
 * a replay after a dropped socket re-sends exactly the range the client missed,
 * a live event that the replay also delivered is dropped here, and reconnecting
 * twice cannot double a position. The server de-duplicates too; this is the
 * second, independent check, because a duplicated position is the failure a
 * trader notices.
 *
 * A **gap** (`seq > cursor + 1`) is not applied silently: the reducer keeps the
 * event, records the gap, and the client asks the server for `RESUME` from its
 * own cursor. Events that arrive while the replay runs are applied normally —
 * the replay's own copies are then ignored by the same cursor rule.
 *
 * ## What is *not* here
 *
 * No balance, no equity, no floating P&L, no margin. Those are the server's
 * numbers (`/preview` → `tryAssessAccount`), and the terminal shows them
 * verbatim. The fold keeps only what the event stream itself defines: order
 * state, filled volume, position volume, SL/TP, and the immutable facts the
 * panels need to label a row.
 *
 * Money-ish fields stay canonical strings exactly as they arrived; nothing is
 * parsed to a float and nothing is recomputed.
 */

import type { AccountSnapshot } from '@weke/protocol';
import type { WireEvent } from '../protocol/events.js';

export type Side = 'BUY' | 'SELL';

export interface OrderRow {
  readonly orderId: string;
  readonly clOrdId: string;
  readonly symbolId: string;
  readonly side: Side;
  readonly type: string;
  readonly tif: string;
  readonly state: string;
  readonly triggerState: string | null;
  readonly volume: string;
  readonly filledVolume: string;
  readonly limitPrice: string | null;
  readonly stopPrice: string | null;
  readonly sl: string | null;
  readonly tp: string | null;
  readonly averageFilledPrice: string | null;
  readonly dealerPending: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface PositionRow {
  readonly positionId: string;
  readonly symbolId: string;
  readonly side: Side;
  readonly volume: string;
  readonly openedVolume: string;
  readonly openPrice: string;
  readonly sl: string | null;
  readonly tp: string | null;
  readonly openedAt: string;
}

export type ActivityKind =
  | 'ORDER_REJECTED'
  | 'DEALER_ACCEPTED'
  | 'POSITION_CLOSED'
  | 'POSITION_REDUCED'
  | 'ALERT'
  | 'STOP_OUT';

export interface ActivityRow {
  readonly id: string;
  readonly at: string;
  readonly kind: ActivityKind;
  readonly symbolId: string | null;
  readonly positionId: string | null;
  readonly orderId: string | null;
  /** A rejection code, an alert kind, or null. */
  readonly code: string | null;
  /** Server-computed money attached to the fact, e.g. a close's realised P&L. */
  readonly amountMinor: string | null;
  readonly detail: string | null;
}

export interface ReplayWindow {
  readonly fromSeq: string;
  readonly throughSeq: string;
  readonly events: number;
  readonly at: string;
}

export interface BookState {
  /** The last `account_seq` applied. Every incoming event must be greater. */
  readonly cursor: bigint;
  readonly orders: Readonly<Record<string, OrderRow>>;
  readonly positions: Readonly<Record<string, PositionRow>>;
  /** Newest first, bounded. */
  readonly activity: readonly ActivityRow[];
  /** Set when an event arrives out of order; the client must RESUME. */
  readonly gapFrom: string | null;
  readonly replay: ReplayWindow | null;
  /** Events applied since the last reset, for the status bar's sanity line. */
  readonly applied: number;
  readonly duplicatesIgnored: number;
}

const ACTIVITY_LIMIT = 100;
const ORDER_LIMIT = 200;

export function initialBook(): BookState {
  return {
    cursor: 0n,
    orders: {},
    positions: {},
    activity: [],
    gapFrom: null,
    replay: null,
    applied: 0,
    duplicatesIgnored: 0,
  };
}

function pushActivity(state: BookState, row: ActivityRow): readonly ActivityRow[] {
  return [row, ...state.activity].slice(0, ACTIVITY_LIMIT);
}

function trimOrders(orders: Record<string, OrderRow>): Record<string, OrderRow> {
  const rows = Object.values(orders);
  if (rows.length <= ORDER_LIMIT) return orders;
  const keep = rows
    .sort((left, right) => (left.updatedAt < right.updatedAt ? -1 : 1))
    .slice(rows.length - ORDER_LIMIT);
  const out: Record<string, OrderRow> = {};
  for (const row of keep) out[row.orderId] = row;
  return out;
}

function orderRow(raw: {
  id: string;
  clOrdId: string;
  symbolId: string;
  side: string;
  type: string;
  tif: string;
  state: string;
  triggerState: string | null;
  volume: string;
  filledVolume: string;
  limitPrice: string | null;
  stopPrice: string | null;
  sl: string | null;
  tp: string | null;
  averageFilledPrice: string | null;
  dealerPending: boolean;
  createdAt: string;
  updatedAt: string;
}): OrderRow {
  return {
    orderId: raw.id,
    clOrdId: raw.clOrdId,
    symbolId: raw.symbolId,
    side: raw.side === 'SELL' ? 'SELL' : 'BUY',
    type: raw.type,
    tif: raw.tif,
    state: raw.state,
    triggerState: raw.triggerState,
    volume: raw.volume,
    filledVolume: raw.filledVolume,
    limitPrice: raw.limitPrice,
    stopPrice: raw.stopPrice,
    sl: raw.sl,
    tp: raw.tp,
    averageFilledPrice: raw.averageFilledPrice,
    dealerPending: raw.dealerPending,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

function withOrder(state: BookState, orderId: string, patch: Partial<OrderRow>): BookState {
  const existing = state.orders[orderId];
  if (existing === undefined) return state;
  const next = { ...existing, ...patch };
  return { ...state, orders: { ...state.orders, [orderId]: next } };
}

/**
 * Apply one event. Pure: same input, same output, and never mutates `state`.
 *
 * Terminal order rows are kept (bounded) so the ticket can show what happened to
 * the order it just placed; positions are removed when they close, because a
 * closed position is history, not a row that should linger with a stale P&L.
 */
export function applyEvent(state: BookState, event: WireEvent): BookState {
  const seq = BigInt(event.seq);
  if (seq <= state.cursor) {
    return { ...state, duplicatesIgnored: state.duplicatesIgnored + 1 };
  }
  const gapFrom = seq > state.cursor + 1n ? state.cursor.toString() : state.gapFrom;
  const base: BookState = {
    ...state,
    cursor: seq,
    gapFrom,
    applied: state.applied + 1,
  };

  switch (event.type) {
    case 'ORDER_RECEIVED': {
      const row = orderRow(event.data.order);
      return { ...base, orders: trimOrders({ ...base.orders, [row.orderId]: row }) };
    }
    case 'ORDER_ACCEPTED': {
      const existing = base.orders[event.data.orderId];
      if (existing === undefined) return base;
      return {
        ...base,
        orders: {
          ...base.orders,
          [existing.orderId]: {
            ...existing,
            state: 'ACTIVE',
            triggerState: event.data.triggerState,
            dealerPending: event.data.dealerPending,
          },
        },
      };
    }
    case 'ORDER_REJECTED':
      return {
        ...withOrder(base, event.data.orderId, { state: 'REJECTED' }),
        activity: pushActivity(base, {
          id: `rej-${event.seq}`,
          at: event.at,
          kind: 'ORDER_REJECTED',
          symbolId: base.orders[event.data.orderId]?.symbolId ?? null,
          positionId: null,
          orderId: event.data.orderId,
          code: event.data.code,
          amountMinor: null,
          detail: event.data.reason,
        }),
      };
    case 'ORDER_MODIFIED':
      return withOrder(base, event.data.orderId, {
        volume: event.data.volume,
        limitPrice: event.data.limitPrice,
        stopPrice: event.data.stopPrice,
        sl: event.data.sl,
        tp: event.data.tp,
        triggerState: event.data.triggerState,
      });
    case 'ORDER_TRIGGERED':
      return withOrder(base, event.data.orderId, { triggerState: event.data.triggerState });
    case 'ORDER_PARTIALLY_FILLED':
    case 'ORDER_FILLED':
      return withOrder(base, event.data.orderId, {
        state: event.type === 'ORDER_FILLED' ? 'FILLED' : 'PARTIALLY_FILLED',
        filledVolume: event.data.filledVolume,
        averageFilledPrice: event.data.averageFilledPrice,
      });
    case 'ORDER_CANCELED':
      return withOrder(base, event.data.orderId, {
        state: 'CANCELED',
        filledVolume: event.data.filledVolume,
      });
    case 'ORDER_EXPIRED':
      return withOrder(base, event.data.orderId, {
        state: 'EXPIRED',
        filledVolume: event.data.filledVolume,
      });
    case 'DEALER_ACCEPTED':
      return {
        ...withOrder(base, event.data.orderId, { dealerPending: false }),
        activity: pushActivity(base, {
          id: `dealer-${event.seq}`,
          at: event.at,
          kind: 'DEALER_ACCEPTED',
          symbolId: base.orders[event.data.orderId]?.symbolId ?? null,
          positionId: null,
          orderId: event.data.orderId,
          code: null,
          amountMinor: null,
          detail: null,
        }),
      };
    case 'DEAL':
      // Deals are the server's ledger; the history panel reads them from
      // `history`. Nothing is folded from them here, so nothing can drift.
      return base;
    case 'POSITION_OPENED': {
      const position = event.data.position;
      const row: PositionRow = {
        positionId: position.id,
        symbolId: position.symbolId,
        side: position.side === 'SELL' ? 'SELL' : 'BUY',
        volume: position.volume,
        openedVolume: position.openedVolume,
        openPrice: position.openPrice,
        sl: position.sl,
        tp: position.tp,
        openedAt: position.openedAt,
      };
      return { ...base, positions: { ...base.positions, [row.positionId]: row } };
    }
    case 'POSITION_MODIFIED':
      return {
        ...base,
        positions:
          base.positions[event.data.positionId] === undefined
            ? base.positions
            : {
                ...base.positions,
                [event.data.positionId]: {
                  ...(base.positions[event.data.positionId] as PositionRow),
                  sl: event.data.sl,
                  tp: event.data.tp,
                },
              },
      };
    case 'POSITION_VOLUME_CHANGED': {
      const existing = base.positions[event.data.positionId];
      if (existing === undefined) return base;
      return {
        ...base,
        positions: {
          ...base.positions,
          [event.data.positionId]: { ...existing, volume: event.data.volume },
        },
        activity: pushActivity(base, {
          id: `vol-${event.seq}`,
          at: event.at,
          kind: 'POSITION_REDUCED',
          symbolId: existing.symbolId,
          positionId: existing.positionId,
          orderId: null,
          code: null,
          amountMinor: event.data.realisedPnlMinor,
          detail: event.data.closedVolume,
        }),
      };
    }
    case 'POSITION_CLOSED': {
      const existing = base.positions[event.data.positionId];
      const positions = { ...base.positions };
      delete positions[event.data.positionId];
      return {
        ...base,
        positions,
        activity: pushActivity(base, {
          id: `close-${event.seq}`,
          at: event.at,
          kind: 'POSITION_CLOSED',
          symbolId: existing?.symbolId ?? null,
          positionId: event.data.positionId,
          orderId: null,
          code: null,
          amountMinor: event.data.realisedPnlMinor,
          detail: event.data.closePrice,
        }),
      };
    }
    default:
      return base;
  }
}

/** Fold a batch (a replay page, or the events buffered during a reconnect). */
export function applyEvents(state: BookState, events: readonly WireEvent[]): BookState {
  let next = state;
  for (const event of events) next = applyEvent(next, event);
  return next;
}

/** Record a `REPLAY_DONE` so the status bar can show what was replayed. */
export function applyReplayDone(
  state: BookState,
  payload: { replayed_through: string; events: number },
  at: string,
  fromSeq: string,
): BookState {
  return {
    ...state,
    replay: { fromSeq, throughSeq: payload.replayed_through, events: payload.events, at },
    gapFrom: null,
  };
}

/**
 * The account snapshot's per-position valuation, by position id.
 *
 * `markE8` and `floatingMinor` are one evaluation, produced by the server from
 * the same `RiskMarket`; the positions table renders **both** the `Now` and the
 * `P&L` cells from this entry (never `Now` from the quote stream), so the row
 * reconciles by construction and both cells go stale together. Reading `Now`
 * from the stream was the P5 defect: a live price next to a polled P&L.
 */
export interface ValuedPosition {
  readonly floatingMinor: string;
  readonly markE8: string;
}

export function valuedPositions(account: AccountSnapshot | null): Record<string, ValuedPosition> {
  const out: Record<string, ValuedPosition> = {};
  if (account === null) return out;
  for (const entry of account.positions) {
    out[entry.position_id] = { floatingMinor: entry.floating_minor, markE8: entry.mark_e8 };
  }
  return out;
}

/** Working orders only, newest first. */
export function workingOrders(book: BookState): readonly OrderRow[] {
  return Object.values(book.orders)
    .filter((order) => order.state === 'ACTIVE' || order.state === 'PARTIALLY_FILLED')
    .sort((left, right) => (left.createdAt < right.createdAt ? 1 : -1));
}

/** Positions in a stable, desk-friendly order: symbol, then oldest first. */
export function sortedPositions(book: BookState): readonly PositionRow[] {
  return Object.values(book.positions).sort((left, right) => {
    if (left.symbolId !== right.symbolId) return left.symbolId < right.symbolId ? -1 : 1;
    return left.openedAt < right.openedAt ? -1 : 1;
  });
}
